import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Typography, Card, Descriptions, Tag, Table, Space, Button, Spin, Alert, Form, Popconfirm, Modal, Select, Tabs, Input, message,
} from 'antd';
import { EditOutlined, PlusOutlined, UserAddOutlined, DisconnectOutlined } from '@ant-design/icons';
import { Link, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext } from '../App';
import { formatTS } from '../utils/time';
import { RecordComments } from '../components/RecordCommentsDrawer';
import ClientTicketsCard from '../components/ClientTicketsCard';
import { ClientFields, ContactModal, STATUS_COLOR, selectProps } from '../components/ClientForms';
import LinksFormList, { cleanLinks } from '../components/LinksFormList';
import { useBackFallback, usePageLabel } from '../components/BackChip';

// Admin > Clients > a client: its details, the contacts assigned to it, and
// its comment log. Everyone with Clients access reads it; Edit and up also
// get Edit (the client's fields), Add Contact, Assign Existing
// contacts, and per contact Edit and Remove from client (unlinks only; the
// contact stays). What is shown follows the Clients list: below Edit
// Manager, only the caller's Visible Campaigns and the contacts on them.
// There is no delete: a client is retired by its Status (Former Client,
// Inactive), keeping its comments, contacts and tickets.

// A contact as the contacts API takes it, from its list row.
const contactPayload = (k, changes = {}) => ({
  first_name:   k.FirstName,
  last_name:    k.LastName,
  title:        k.Title || '',
  status:       k.Status || 'Active',
  email:        k.Email || '',
  phone:        k.Phone || '',
  cell:         k.Cell || '',
  client_ids:   k.ClientIDs || [],
  campaign_ids: k.CampaignIDs || [],
  team_ids:     k.TeamIDs || [],
  ...changes,
});

export default function ClientDetailPage() {
  const { id } = useParams();
  const clientId = Number(id);
  const currentUser = useContext(UserContext);
  const canEdit = (currentUser?.CanEditClients ?? 0) >= 2;

  const [client, setClient]     = useState(null);
  // Names this page in Recent and on the next page's Back chip.
  usePageLabel(client?.Name ? `Client: ${client.Name}` : null);
  // The Back chip (Layout): where you came from, else Clients.
  useBackFallback('/admin/clients', 'Clients');
  const [clients, setClients]   = useState([]);    // list rows: for the contact popup's campaign/team choices
  const [contacts, setContacts] = useState([]);    // list rows: full contacts for edit / assign
  const [options, setOptions]   = useState({ campaigns: [], teams: [], queues: [] });
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const [editing, setEditing]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [contactModal, setContactModal] = useState(null);   // null | { contact }
  const [assignOpen, setAssignOpen]     = useState(false);
  const [assignIds, setAssignIds]       = useState([]);
  // Contacts open on Active; Inactive (retired) ones are a tab away.
  const [contactTab, setContactTab]     = useState('Active');
  const [form] = Form.useForm();
  // Campaign goals in force this month on the client's campaigns (from the
  // Goals page, read-only). null = loading.
  const [goals, setGoals] = useState(null);
  // Contract Details card: edited on its own, like the Client card.
  const [contractEditing, setContractEditing] = useState(false);
  const [contractForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, cl, co, op] = await Promise.all([
        api.get(`/api/clients/${clientId}`),
        api.get('/api/clients'),
        api.get('/api/client-contacts'),
        api.get('/api/clients/options'),
      ]);
      setClient(c.data);
      setClients(cl.data || []);
      setContacts(co.data || []);
      setOptions(op.data || { campaigns: [], teams: [] });
      setError(null);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Client not found.' : 'Failed to load the client.');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/api/goals').then((r) => setGoals(r.data || [])).catch(() => setGoals([]));
  }, []);

  const contactById = useMemo(() => Object.fromEntries(contacts.map((k) => [k.ID, k])), [contacts]);

  if (loading && !client) return <Spin />;
  if (error) return <Alert type="error" showIcon message={error} />;
  if (!client) return null;

  const campaignIDs = (client.Campaigns || []).map((c) => c.ID);
  const teamIDs     = (client.Teams || []).map((t) => t.ID);

  // -- client ------------------------------------------------------------------

  const startEdit = () => {
    form.setFieldsValue({
      name: client.Name, state: client.State || '', country: client.Country || '',
      status: client.Status || 'Client', campaign_ids: campaignIDs, team_ids: teamIDs,
      helpdesk_queue_id: client.HelpDeskQueueID ?? undefined,
    });
    setEditing(true);
  };

  const saveClient = async () => {
    let v;
    try { v = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      // Explicit null: a cleared queue would otherwise be left out of the
      // request, which the API reads as "unchanged".
      await api.put(`/api/clients/${clientId}`, { ...v, helpdesk_queue_id: v.helpdesk_queue_id ?? null });
      message.success('Client saved.');
      setEditing(false);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // -- contract details ----------------------------------------------------------

  const startContractEdit = () => {
    contractForm.setFieldsValue({
      contract_details:   client.ContractDetails || '',
      hours_of_operation: client.HoursOfOperation || '',
      links: (client.Links || []).map((l) => ({ url: l.URL, label: l.Label || '' })),
    });
    setContractEditing(true);
  };

  const saveContract = async () => {
    let v;
    try { v = await contractForm.validateFields(); } catch { return; }
    setSaving(true);
    try {
      await api.put(`/api/clients/${clientId}/contract`, {
        contract_details:   v.contract_details || '',
        hours_of_operation: v.hours_of_operation || '',
        links: cleanLinks(v.links),
      });
      message.success('Contract details saved.');
      setContractEditing(false);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const paragraph = (text) => (text
    ? <div style={{ whiteSpace: 'pre-wrap' }}>{text}</div>
    : <Typography.Text type="secondary">—</Typography.Text>);

  // -- campaign goals ------------------------------------------------------------

  // Active = in force this month: started by now, and open-ended or not yet
  // ended. Only the client's own campaigns (not the Overall row).
  const thisMonth = dayjs().format('YYYY-MM');
  const campIds = new Set(campaignIDs);
  const activeGoals = (goals || [])
    .filter((x) => campIds.has(x.CampaignID) && x.StartMonth <= thisMonth && (!x.EndMonth || x.EndMonth >= thisMonth))
    .sort((a, b) => (a.CampaignName || '').localeCompare(b.CampaignName || '') || (a.label || '').localeCompare(b.label || ''));
  const goalVal = (v, r) => {
    if (v == null) return <Typography.Text type="secondary">—</Typography.Text>;
    if (r.format === 'mmss') return `${Math.floor(v / 60)}:${String(Math.floor(v % 60)).padStart(2, '0')}`;
    if (r.format === 'pct') return `${v}%`;
    return v;
  };
  const monthLabel = (v) => (v ? dayjs(`${v}-01`).format('MMM YYYY') : null);

  // -- contacts ----------------------------------------------------------------

  // Unlink only. The contact keeps its other clients, and the campaigns and
  // teams those still give it.
  const removeContact = async (k) => {
    const full = contactById[k.ID];
    if (!full) return;
    const remaining = (full.ClientIDs || []).filter((cid) => cid !== clientId);
    const kept = clients.filter((c) => remaining.includes(c.ID));
    const camps = new Set(kept.flatMap((c) => c.CampaignIDs || []));
    const teams = new Set(kept.flatMap((c) => c.TeamIDs || []));
    try {
      await api.put(`/api/client-contacts/${k.ID}`, contactPayload(full, {
        client_ids:   remaining,
        campaign_ids: (full.CampaignIDs || []).filter((x) => camps.has(x)),
        team_ids:     (full.TeamIDs || []).filter((x) => teams.has(x)),
      }));
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Could not remove the contact.');
    }
  };

  const assignable = contacts.filter((k) => !(k.ClientIDs || []).includes(clientId));

  const assignContacts = async () => {
    setSaving(true);
    try {
      for (const cid of assignIds) {
        const k = contactById[cid];
        if (k) {
          await api.put(`/api/client-contacts/${cid}`,
            contactPayload(k, { client_ids: [...(k.ClientIDs || []), clientId] }));
        }
      }
      setAssignOpen(false);
      setAssignIds([]);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Could not assign the contact.');
    } finally {
      setSaving(false);
    }
  };

  const commentRecord = {
    kind: 'clients',
    id: client.ID,
    name: client.Name,
    campaigns: (client.Campaigns || []).map((c) => ({ value: c.ID, label: c.Name })),
  };

  return (
    <div>
      <Space align="center" style={{ marginBottom: 16 }} wrap>
        <Typography.Title level={3} style={{ margin: 0 }}>{client.Name}</Typography.Title>
        {client.Status && <Tag color={STATUS_COLOR[client.Status]}>{client.Status}</Tag>}
      </Space>

      <Card
        title="Client"
        size="small"
        style={{ marginBottom: 16 }}
        extra={canEdit && (editing ? (
          <Space>
            <Button size="small" onClick={() => setEditing(false)}>Cancel</Button>
            <Button size="small" type="primary" loading={saving} onClick={saveClient}>Save</Button>
          </Space>
        ) : (
          <Button size="small" icon={<EditOutlined />} onClick={startEdit}>Edit</Button>
        ))}
      >
        {editing ? (
          <Form form={form} layout="vertical" style={{ maxWidth: 720 }}>
            <ClientFields options={options} campaignIDs={campaignIDs} teamIDs={teamIDs} queueID={client.HelpDeskQueueID} />
          </Form>
        ) : (
          <Descriptions size="small" bordered column={{ xs: 1, md: 2, lg: 3 }}>
            <Descriptions.Item label="Name">{client.Name}</Descriptions.Item>
            <Descriptions.Item label="State">{client.State || '—'}</Descriptions.Item>
            <Descriptions.Item label="Country">{client.Country || '—'}</Descriptions.Item>
            <Descriptions.Item label="Status">{client.Status || '—'}</Descriptions.Item>
            <Descriptions.Item label="Help Desk Queue" span={2}>{client.HelpDeskQueueName || '—'}</Descriptions.Item>
            <Descriptions.Item label="Campaigns" span={3}>
              {(client.Campaigns || []).length
                ? <Space size={[0, 4]} wrap>{client.Campaigns.map((c) => <Tag key={c.ID}>{c.Name}</Tag>)}</Space>
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Teams" span={3}>
              {(client.Teams || []).length
                ? <Space size={[0, 4]} wrap>{client.Teams.map((t) => <Tag key={t.ID}>{t.Name}</Tag>)}</Space>
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Created">{formatTS(client.CreatedAt)}</Descriptions.Item>
            <Descriptions.Item label="Updated">{formatTS(client.UpdatedAt)}</Descriptions.Item>
          </Descriptions>
        )}
      </Card>

      <Card
        title="Contacts"
        size="small"
        style={{ marginBottom: 16 }}
        extra={canEdit && (
          <Space>
            <Button size="small" icon={<UserAddOutlined />} onClick={() => { setAssignIds([]); setAssignOpen(true); }}
              disabled={!assignable.length}>
              Assign Existing
            </Button>
            <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setContactModal({ contact: null })}>
              Add Contact
            </Button>
          </Space>
        )}
      >
        <Tabs
          size="small"
          activeKey={contactTab}
          onChange={setContactTab}
          items={['Active', 'Inactive'].map((st) => ({
            key: st,
            label: `${st} (${(client.Contacts || []).filter((k) => (st === 'Inactive') === (k.Status === 'Inactive')).length})`,
          }))}
        />
        <Table
          rowKey="ID"
          dataSource={(client.Contacts || []).filter((k) => (contactTab === 'Inactive') === (k.Status === 'Inactive'))}
          size="small"
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: contactTab === 'Inactive' ? 'No inactive contacts.' : 'No active contacts are assigned to this client.' }}
          columns={[
            { title: 'Name', key: 'Name', width: 180,
              sorter: (a, b) => `${a.LastName} ${a.FirstName}`.localeCompare(`${b.LastName} ${b.FirstName}`),
              render: (_, k) => <Link to={`/admin/client-contacts/${k.ID}`}>{k.FirstName} {k.LastName}</Link> },
            { title: 'Title', dataIndex: 'Title', key: 'Title', width: 160, render: (v) => v || '—' },
            { title: 'Email', dataIndex: 'Email', key: 'Email', width: 220,
              render: (v) => (v ? <a href={`mailto:${v}`}>{v}</a> : '—') },
            { title: 'Phone', dataIndex: 'Phone', key: 'Phone', width: 130, render: (v) => v || '—' },
            { title: 'Cell', dataIndex: 'Cell', key: 'Cell', width: 130, render: (v) => v || '—' },
            { title: 'Status', dataIndex: 'Status', key: 'Status', width: 100,
              render: (v) => (v ? <Tag color={STATUS_COLOR[v]}>{v}</Tag> : '—') },
            { title: 'Campaigns', dataIndex: 'Campaigns', key: 'Campaigns',
              render: (v) => ((v || []).length
                ? <Space size={[0, 4]} wrap>{v.map((n) => <Tag key={n}>{n}</Tag>)}</Space>
                : '—') },
            ...(!canEdit ? [] : [{ title: '', key: 'actions', width: 210, align: 'right',
              render: (_, k) => (
                <Space>
                  <Button size="small" icon={<EditOutlined />} disabled={!contactById[k.ID]}
                    onClick={() => setContactModal({ contact: contactById[k.ID] })}>Edit</Button>
                  <Popconfirm title={`Remove ${k.FirstName} ${k.LastName} from ${client.Name}?`}
                    description="The contact is not deleted." okText="Remove" onConfirm={() => removeContact(k)}>
                    <Button size="small" icon={<DisconnectOutlined />}>Remove</Button>
                  </Popconfirm>
                </Space>
              ) }]),
          ]}
        />
      </Card>

      <Card
        title="Contract Details"
        size="small"
        style={{ marginBottom: 16 }}
        extra={canEdit && (contractEditing ? (
          <Space>
            <Button size="small" onClick={() => setContractEditing(false)}>Cancel</Button>
            <Button size="small" type="primary" loading={saving} onClick={saveContract}>Save</Button>
          </Space>
        ) : (
          <Button size="small" icon={<EditOutlined />} onClick={startContractEdit}>Edit</Button>
        ))}
      >
        {contractEditing ? (
          <Form form={contractForm} layout="vertical" style={{ maxWidth: 900 }}>
            <Form.Item name="contract_details" label="Contract Details">
              <Input.TextArea autoSize={{ minRows: 3, maxRows: 16 }} />
            </Form.Item>
            <Form.Item name="hours_of_operation" label="Hours of Operation">
              <Input.TextArea autoSize={{ minRows: 2, maxRows: 10 }} />
            </Form.Item>
            <Form.Item label="Links">
              <LinksFormList name="links" />
            </Form.Item>
          </Form>
        ) : (
          <Descriptions size="small" bordered column={1}>
            <Descriptions.Item label="Contract Details">{paragraph(client.ContractDetails)}</Descriptions.Item>
            <Descriptions.Item label="Hours of Operation">{paragraph(client.HoursOfOperation)}</Descriptions.Item>
            <Descriptions.Item label="Links">
              {(client.Links || []).length ? (
                <Space direction="vertical" size={2}>
                  {client.Links.map((l) => (
                    <a key={l.ID} href={l.URL} target="_blank" rel="noopener noreferrer">{l.Label || l.URL}</a>
                  ))}
                </Space>
              ) : <Typography.Text type="secondary">—</Typography.Text>}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Card>

      {/* From the call data (each campaign's latest day); a skill is named
          only when its campaign has more than one setting. */}
      <Card title="Current SLA" size="small" style={{ marginBottom: 16 }}>
        <Table
          rowKey={(r) => `${r.CampaignID}|${r.SLATime}|${r.SLAGoal}|${r.ShortAbandonTime}`}
          size="small"
          dataSource={client.CurrentSLA || []}
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: client.CurrentSLA === null ? 'Call data unavailable.' : "No SLA found in this client's call data." }}
          columns={[
            { title: 'Campaign', dataIndex: 'CampaignName', key: 'CampaignName', width: 170 },
            ...((client.CurrentSLA || []).some((r) => r.Skills.length)
              ? [{ title: 'Skills', dataIndex: 'Skills', key: 'Skills', width: 260,
                  render: (v) => (v.length ? v.join(', ') : '') }]
              : []),
            { title: 'SLA Goal', dataIndex: 'SLAGoal', key: 'SLAGoal', width: 90, align: 'right',
              render: (v) => (v == null ? '—' : `${Math.round(v * 1000) / 10}%`) },
            { title: 'SLA Time', dataIndex: 'SLATime', key: 'SLATime', width: 90, align: 'right',
              render: (v) => (v == null ? '—' : `${v} sec`) },
            { title: 'Short Abandon Time', dataIndex: 'ShortAbandonTime', key: 'ShortAbandonTime', width: 150, align: 'right',
              render: (v) => (v == null ? '—' : `${v} sec`) },
            { title: 'As Of', dataIndex: 'AsOf', key: 'AsOf', width: 110, render: (v) => dayjs(v).format('MMM D, YYYY') },
          ]}
        />
      </Card>

      <Card title="Active Campaign Goals" size="small" style={{ marginBottom: 16 }}>
        <Table
          rowKey="ID"
          size="small"
          loading={goals === null}
          dataSource={activeGoals}
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: "No active goals on this client's campaigns." }}
          columns={[
            { title: 'Campaign', dataIndex: 'CampaignName', key: 'CampaignName', width: 170 },
            { title: 'Metric', dataIndex: 'label', key: 'label', width: 210 },
            { title: 'Applies To', dataIndex: 'FullTime', key: 'FullTime', width: 110,
              render: (v) => (v == null ? <Typography.Text type="secondary">All</Typography.Text>
                : v ? <Tag color="blue">Full Time</Tag> : <Tag>Part Time</Tag>) },
            { title: 'Silver', dataIndex: 'SilverValue', key: 'SilverValue', width: 90, align: 'right', render: goalVal },
            { title: 'Gold', dataIndex: 'TargetValue', key: 'TargetValue', width: 90, align: 'right', render: goalVal },
            { title: 'Platinum', dataIndex: 'PlatinumValue', key: 'PlatinumValue', width: 90, align: 'right', render: goalVal },
            { title: 'Start Month', dataIndex: 'StartMonth', key: 'StartMonth', width: 110, render: monthLabel },
            { title: 'End Month', dataIndex: 'EndMonth', key: 'EndMonth', width: 110,
              render: (v) => monthLabel(v) || <Tag>Open-ended</Tag> },
          ]}
        />
      </Card>

      <ClientTicketsCard params={{ client_ids: clientId }} />

      <Card title="Comments" size="small">
        <RecordComments record={commentRecord} canAdd={canEdit} onChanged={() => {}} />
      </Card>

      <ContactModal
        open={!!contactModal}
        contact={contactModal?.contact}
        presetClientIds={[clientId]}
        clients={clients}
        options={options}
        onClose={() => setContactModal(null)}
        onSaved={() => { setContactModal(null); load(); }}
      />

      <Modal maskClosable={false}
        title={`Assign Contacts to ${client.Name}`}
        open={assignOpen}
        onCancel={() => setAssignOpen(false)}
        onOk={assignContacts}
        okText="Assign"
        okButtonProps={{ disabled: !assignIds.length }}
        confirmLoading={saving}
        destroyOnClose
      >
        <Select
          {...selectProps}
          value={assignIds}
          onChange={setAssignIds}
          placeholder="Pick contacts"
          options={assignable.map((k) => ({
            value: k.ID,
            label: `${k.FirstName} ${k.LastName}${k.Title ? ` (${k.Title})` : ''}`,
          }))}
        />
      </Modal>
    </div>
  );
}
